import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Crown, Zap, Loader2, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { isAndroidWebView } from "@/lib/platform";
import { GooglePlayBadge } from "@/components/GooglePlayBadge";

const PLANS = [
  {
    key: "base",
    nome: "Base",
    icon: Zap,
    monthly: { lookup: "base_monthly", label: "29€", sub: "/mese" },
    yearly: { lookup: "base_yearly", label: "290€", sub: "/anno (2 mesi gratis)" },
    features: [
      "Rapportini illimitati",
      "Fino a 10 dipendenti",
      "Vidimazione e stampe PDF",
      "500 email al mese",
      "Funziona offline (PWA)",
    ],
  },
  {
    key: "pro",
    nome: "Pro",
    icon: Crown,
    highlight: true,
    monthly: { lookup: "pro_monthly", label: "49€", sub: "/mese" },
    yearly: { lookup: "pro_yearly", label: "490€", sub: "/anno (2 mesi gratis)" },
    features: [
      "Tutto del piano Base",
      "Dipendenti illimitati",
      "Email illimitate",
      "Supporto prioritario",
    ],
  },
  {
    key: "pro_ai",
    nome: "Pro + IA",
    icon: Sparkles,
    monthly: { lookup: "pro_ai_monthly", label: "79€", sub: "/mese" },
    yearly: { lookup: "pro_ai_yearly", label: "790€", sub: "/anno (2 mesi gratis)" },
    features: [
      "Tutto del piano Pro",
      "Import del programma giornaliero da Word con lettura IA",
      "Assistente \"Chiedi\": domande su chi era dove, mezzi e assenze",
      "60 operazioni IA al mese incluse",
      "Scansione AI rapportini cartacei",
    ],
  },
];

export default function AbbonamentoPage() {
  const { user } = useAuth();
  const [yearly, setYearly] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const inApp = isAndroidWebView();

  const handleCheckout = async (lookup) => {
    setLoadingKey(lookup);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore avvio pagamento");
      setLoadingKey(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      <div className="text-center space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-heading font-black tracking-tight">Abbonamento</h1>
          <GooglePlayBadge />
        </div>
        <p className="text-sm text-slate-500">
          {user?.plan && user.plan !== "trial" ? (
            <>Piano attuale: <b className="uppercase">{user.plan}</b></>
          ) : (
            <>Scegli il piano per la tua azienda {user?.company_name && <b>{user.company_name}</b>}</>
          )}
        </p>
        <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full p-1 mt-2">
          <button
            type="button"
            onClick={() => setYearly(false)}
            data-testid="billing-monthly-toggle"
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${!yearly ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
          >
            Mensile
          </button>
          <button
            type="button"
            onClick={() => setYearly(true)}
            data-testid="billing-yearly-toggle"
            className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${yearly ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
          >
            Annuale −20%
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PLANS.map((p) => {
          const price = yearly ? p.yearly : p.monthly;
          const Icon = p.icon;
          return (
            <Card
              key={p.key}
              data-testid={`plan-card-${p.key}`}
              className={`p-6 space-y-5 relative ${p.highlight ? "border-2 border-[#0044FF] shadow-lg" : ""}`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0044FF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Consigliato
                </span>
              )}
              <div className="flex items-center gap-2">
                <Icon className={`h-6 w-6 ${p.highlight ? "text-[#0044FF]" : "text-slate-600"}`} />
                <h2 className="font-heading font-black text-xl">{p.nome}</h2>
              </div>
              <div>
                <span className="text-4xl font-heading font-black">{price.label}</span>
                <span className="text-sm text-slate-500 ml-1">{price.sub}</span>
              </div>
              <ul className="space-y-2">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {inApp ? (
                <div className="text-xs text-slate-500 text-center border rounded-md py-3 px-2" data-testid={`plan-inapp-note-${p.key}`}>Attivabile dal sito web</div>
              ) : (
              <Button
                onClick={() => handleCheckout(price.lookup)}
                disabled={!!loadingKey}
                data-testid={`plan-subscribe-${p.key}`}
                className={`w-full h-12 font-bold ${p.highlight ? "bg-[#0044FF] hover:bg-[#0033CC]" : ""}`}
                variant={p.highlight ? "default" : "outline"}
              >
                {loadingKey === price.lookup ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  `Attiva ${p.nome}`
                )}
              </Button>
              )}
            </Card>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-400">
        Pagamento sicuro con Stripe · Disdici quando vuoi · Prezzi IVA esclusa
      </p>
    </div>
  );
}
